// src/lib/morningMeeting.js
// STATUS: active | UPDATED: 2026-09-23
// LOG:
//   2026-09-23  New. See migration 0040 for why this table has these columns.
//               Shape and the prepared/held distinction live in
//               morningMeetingShape.js so they can be tested.
//
// Jazz's first daily discipline, as an agenda written before the meeting.

import { supabase } from './supabase'
import { dayKey, addDays } from './localDate'

export * from './morningMeetingShape'

const COLS = `
  id, dealership_id, led_by, meeting_on,
  results, appointments, deliveries, aged,
  skill_focus, step_number, held_at, attended, updated_at
`

export async function getMeeting(dealershipId, on) {
  if (!dealershipId) return { data: null, error: null }
  const { data, error } = await supabase
    .from('morning_meetings')
    .select(COLS)
    .eq('dealership_id', dealershipId)
    .eq('meeting_on', on)
    .maybeSingle()
  return { data: data || null, error }
}

export async function listRecent(dealershipId, limit = 14) {
  if (!dealershipId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('morning_meetings')
    .select(COLS)
    .eq('dealership_id', dealershipId)
    .order('meeting_on', { ascending: false })
    .limit(limit)
  return { data: data || [], error }
}

// UPSERT ON (dealership_id, meeting_on), unique since 0040. A second save is a
// correction, never a second meeting.
//
// Empty strings are stored as null so "not prepared" and "prepared, nothing to
// say" do not look identical to `meetingState` later.
export async function saveMeeting({ dealershipId, ledBy, on, fields }) {
  if (!dealershipId) return { data: null, error: 'No dealership on your account.' }
  const row = { dealership_id: dealershipId, led_by: ledBy, meeting_on: on || dayKey() }
  for (const [k, v] of Object.entries(fields || {})) {
    if (k === 'step_number') {
      row[k] = v === '' || v == null ? null : Number(v)
      continue
    }
    const text = typeof v === 'string' ? v.trim() : v
    row[k] = text === '' || text == null ? null : text
  }
  const { data, error } = await supabase
    .from('morning_meetings')
    .upsert(row, { onConflict: 'dealership_id,meeting_on' })
    .select(COLS)
    .single()
  return { data, error: error ? error.message || String(error) : null }
}

// Stamped when it actually happened, with who was in the room. Separate from
// saving the agenda, because preparing and holding are different acts and the
// gap between them is what this table can see that paper cannot.
export async function markHeld({ id, attended }) {
  const { data, error } = await supabase
    .from('morning_meetings')
    .update({ held_at: new Date().toISOString(), attended: attended || [] })
    .eq('id', id)
    .select(COLS)
    .single()
  return { data, error: error ? error.message || String(error) : null }
}

// The last N days as keys, newest first, so a run of days can be shown against
// what was actually held.
export function lastDays(n, from = dayKey()) {
  const out = []
  for (let i = 0; i < n; i += 1) out.push(addDays(from, -i))
  return out
}
